import {Flag} from 'lucide-react';
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {FLAG_COLORS} from '@/api/flags';
import {FlagIcon, useFlagLabel} from '@/components/flags/Flag';
import {toggleFlagFilter, type FlagFilter} from '@/lib/flagFilter';
import {FilterChip} from '@/components/shared/FilterChip';

// "My flag" filter shared by Items and Investigations. Controlled: the caller owns where the
// selection lives (the URL for Items, component state for Investigations).
export function FlagFilterMenu({
  value,
  onChange
}: {
  readonly value: readonly FlagFilter[];
  readonly onChange: (next: FlagFilter[]) => void;
}) {
  const labelOf = useFlagLabel();
  const colours = new Set(value.filter((f) => f !== 'any'));
  const onlyColour = value.length === 1 && value[0] !== 'any' ? value[0] : undefined;
  const active = value.length > 0;
  // Base UI checkbox items leave the menu open after a pick (closeOnClick defaults to false), so
  // several colours can be chosen in one visit without the Radix-style preventDefault.

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={active ? `Filter by my flag, ${value.length} selected` : 'Filter by my flag'}
        render={
          <FilterChip
            active={active}
            icon={onlyColour ? <FlagIcon color={onlyColour} /> : <Flag />}
            label="Flags"
            count={value.length}
          />
        }
      />
      <DropdownMenuContent align="start">
        <DropdownMenuGroup>
          <DropdownMenuLabel>My flag</DropdownMenuLabel>
          <DropdownMenuCheckboxItem
            checked={value.includes('any')}
            onCheckedChange={() => {
              onChange(toggleFlagFilter(value, 'any'));
            }}>
            Any flag
          </DropdownMenuCheckboxItem>
          {FLAG_COLORS.map((color) => (
            <DropdownMenuCheckboxItem
              key={color}
              checked={colours.has(color)}
              onCheckedChange={() => {
                onChange(toggleFlagFilter(value, color));
              }}>
              <FlagIcon color={color} />
              {labelOf(color)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuGroup>
        {active ? (
          <>
            <DropdownMenuSeparator />
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start"
              onClick={() => {
                onChange([]);
              }}>
              Clear
            </Button>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Plain checkbox body of the flag filter, for panels that are not a dropdown menu.
export function FlagFilterFields({
  value,
  onChange
}: {
  readonly value: readonly FlagFilter[];
  readonly onChange: (next: FlagFilter[]) => void;
}) {
  const labelOf = useFlagLabel();
  return (
    <div role="group" aria-label="My flag" className="space-y-0.5">
      <div className="text-muted-foreground px-1.5 py-1 text-xs">My flag</div>
      {(['any', ...FLAG_COLORS] as const).map((flag) => (
        <label key={flag} className="flex items-center gap-2 px-1.5 py-1 text-xs">
          <Checkbox
            checked={value.includes(flag)}
            onCheckedChange={() => {
              onChange(toggleFlagFilter(value, flag));
            }}
          />
          {flag === 'any' ? <Flag className="size-3.5" /> : <FlagIcon color={flag} />}
          {flag === 'any' ? 'Any flag' : labelOf(flag)}
        </label>
      ))}
    </div>
  );
}

// The active filters as removable chips, with "Clear all". Renders nothing when nothing is active.
export function FlagFilterChips({
  value,
  onChange
}: {
  readonly value: readonly FlagFilter[];
  readonly onChange: (next: FlagFilter[]) => void;
}) {
  const labelOf = useFlagLabel();
  if (value.length === 0) {
    return null;
  }

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      role="group"
      aria-label="Active flag filters">
      {value.map((f) => (
        <Button
          key={f}
          variant="secondary"
          size="xs"
          aria-label={`Remove ${f === 'any' ? 'any flag' : labelOf(f)} filter`}
          onClick={() => {
            onChange(value.filter((v) => v !== f));
          }}>
          {f === 'any' ? <Flag /> : <FlagIcon color={f} />}
          {f === 'any' ? 'Any flag' : labelOf(f)}
          <span aria-hidden="true">×</span>
        </Button>
      ))}
      <Button
        variant="link"
        size="xs"
        onClick={() => {
          onChange([]);
        }}>
        Clear all
      </Button>
    </div>
  );
}
